import React, { useState, useEffect, useCallback } from 'react';
import { 
  CheckCircle2, XCircle, AlertTriangle, Eye, Brain, 
  Target, Building2, FileText, RefreshCw, ChevronRight,
  ThumbsUp, ThumbsDown, Edit2, Check, X, Clock,
  TrendingUp, Zap, Filter, Search
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { apiFetch } from '../services/api_service';


const API_URL = process.env.REACT_APP_BACKEND_URL;

// Module configuration
const MODULE_CONFIG = {
  sbti_tracking: { 
    icon: Target, 
    label: 'SBTi Target Tracking',
    color: 'text-green-600',
    bgColor: 'bg-green-100'
  },
  csddd: { 
    icon: Building2, 
    label: 'CSDDD Due Diligence',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100'
  },
  group_consolidation: { 
    icon: TrendingUp, 
    label: 'Group Consolidation',
    color: 'text-purple-600',
    bgColor: 'bg-purple-100'
  }
};

// Confidence level styling
const CONFIDENCE_CONFIG = {
  high: { color: 'bg-green-100 text-green-700 border-green-200', label: 'High Confidence', icon: CheckCircle2 },
  medium: { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', label: 'Medium Confidence', icon: AlertTriangle },
  low: { color: 'bg-red-100 text-red-700 border-red-200', label: 'Low Confidence', icon: XCircle }
};

// Stats Card Component
const StatCard = ({ title, value, icon: Icon, color, subtext }) => (
  <Card>
    <CardContent className="pt-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
          {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Review Item Card Component
const ReviewItemCard = ({ item, onAction, onViewDetail }) => {
  const [loading, setLoading] = useState(false);
  const moduleConfig = MODULE_CONFIG[item.module] || MODULE_CONFIG.sbti_tracking;
  const ModuleIcon = moduleConfig.icon;
  
  const dataQuality = item.data_quality || 'medium';
  const confidenceConfig = CONFIDENCE_CONFIG[dataQuality];
  const ConfidenceIcon = confidenceConfig.icon;

  const handleAction = async (action) => {
    setLoading(true);
    try {
      await onAction(item.module, item.record_id, action);
    } finally {
      setLoading(false);
    }
  };

  // Format emissions data for SBTi items
  const formatEmissions = (totals) => {
    if (!totals) return null;
    return (
      <div className="grid grid-cols-4 gap-2 text-sm mt-2">
        <div className="text-center p-2 bg-gray-50 rounded">
          <p className="text-xs text-gray-500">Scope 1</p>
          <p className="font-semibold">{totals.total_scope1_tco2e?.toLocaleString() || 0}</p>
        </div>
        <div className="text-center p-2 bg-gray-50 rounded">
          <p className="text-xs text-gray-500">Scope 2</p>
          <p className="font-semibold">{totals.total_scope2_tco2e?.toLocaleString() || 0}</p>
        </div>
        <div className="text-center p-2 bg-gray-50 rounded">
          <p className="text-xs text-gray-500">Scope 3</p>
          <p className="font-semibold">{totals.total_scope3_tco2e?.toLocaleString() || 0}</p>
        </div>
        <div className="text-center p-2 bg-green-50 rounded">
          <p className="text-xs text-gray-500">Total</p>
          <p className="font-bold text-green-700">{totals.total_emissions_tco2e?.toLocaleString() || 0}</p>
        </div>
      </div>
    );
  };

  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`review-item-${item.record_id}`}>
      <CardContent className="pt-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${moduleConfig.bgColor}`}>
              <ModuleIcon className={`h-5 w-5 ${moduleConfig.color}`} />
            </div>
            <div>
              <p className="font-medium">{item.source_file || 'Unknown Source'}</p>
              <p className="text-sm text-gray-500">{moduleConfig.label}</p>
            </div>
          </div>
          <Badge className={confidenceConfig.color}>
            <ConfidenceIcon className="h-3 w-3 mr-1" />
            {confidenceConfig.label}
          </Badge>
        </div>

        {/* Emissions Summary for SBTi */}
        {item.module === 'sbti_tracking' && item.annual_totals && (
          <div className="mb-3">
            <p className="text-xs text-gray-500 mb-1">
              Emissions (tCO2e) • Period: {item.reporting_period || 'Unknown'}
            </p>
            {formatEmissions(item.annual_totals)}
          </div>
        )}

        {/* Facilities */}
        {item.facilities?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {item.facilities.map((facility, idx) => (
              <Badge key={idx} variant="outline" className="text-xs">
                {facility}
              </Badge>
            ))}
          </div>
        )}

        {/* Timestamp */}
        <p className="text-xs text-gray-400 mb-3">
          <Clock className="h-3 w-3 inline mr-1" />
          Extracted: {new Date(item.created_at).toLocaleString()}
        </p>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 pt-2 border-t">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => onViewDetail(item)}
            className="flex-1"
          >
            <Eye className="h-4 w-4 mr-1" />
            Review
          </Button>
          <Button 
            variant="default" 
            size="sm" 
            onClick={() => handleAction('approve')}
            disabled={loading}
            className="flex-1 bg-green-600 hover:bg-green-700"
          >
            <ThumbsUp className="h-4 w-4 mr-1" />
            {loading ? 'Processing...' : 'Approve'}
          </Button>
          <Button 
            variant="destructive" 
            size="sm" 
            onClick={() => handleAction('reject')}
            disabled={loading}
          >
            <ThumbsDown className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Detail Modal Component
const ReviewDetailModal = ({ item, onClose, onAction }) => {
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedTotals, setEditedTotals] = useState(item?.annual_totals || {});
  const [year, setYear] = useState(new Date().getFullYear());
  const [notes, setNotes] = useState('');

  if (!item) return null;

  const moduleConfig = MODULE_CONFIG[item.module] || MODULE_CONFIG.sbti_tracking;
  const ModuleIcon = moduleConfig.icon;

  const handleAction = async (action) => {
    setLoading(true);
    try {
      const corrections = editMode && action === 'approve' ? { annual_totals: editedTotals } : null;
      await onAction(item.module, item.record_id, action, { year, notes, corrections });
      onClose();
    } catch (error) {
      toast.error('Action failed');
    } finally {
      setLoading(false);
    }
  };

  const handleEditChange = (field, value) => {
    setEditedTotals(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-3xl w-full max-h-[90vh] overflow-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-lg ${moduleConfig.bgColor}`}>
                <ModuleIcon className={`h-6 w-6 ${moduleConfig.color}`} />
              </div>
              <div>
                <CardTitle>Review AI Extraction</CardTitle>
                <CardDescription>{item.source_file}</CardDescription>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Source Info */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium mb-2">Source Information</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Upload ID</p>
                <p className="font-mono text-xs">{item.upload_id}</p>
              </div>
              <div>
                <p className="text-gray-500">Reporting Period</p>
                <p>{item.reporting_period || 'Not specified'}</p>
              </div>
              <div>
                <p className="text-gray-500">Data Quality</p>
                <Badge className={CONFIDENCE_CONFIG[item.data_quality || 'medium'].color}>
                  {item.data_quality || 'Medium'}
                </Badge>
              </div>
              <div>
                <p className="text-gray-500">Facilities</p>
                <p>{item.facilities?.join(', ') || 'None specified'}</p>
              </div>
            </div>
          </div>

          {/* Emissions Data for SBTi */}
          {item.module === 'sbti_tracking' && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium">Emissions Data (tCO2e)</h4>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setEditMode(!editMode)}
                >
                  <Edit2 className="h-4 w-4 mr-1" />
                  {editMode ? 'Cancel Edit' : 'Edit Values'}
                </Button>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                {[
                  { key: 'total_scope1_tco2e', label: 'Scope 1' },
                  { key: 'total_scope2_tco2e', label: 'Scope 2' },
                  { key: 'total_scope3_tco2e', label: 'Scope 3' },
                  { key: 'total_emissions_tco2e', label: 'Total Emissions' }
                ].map(({ key, label }) => (
                  <div key={key} className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">{label}</p>
                    {editMode ? (
                      <Input
                        type="number"
                        value={editedTotals[key] || 0}
                        onChange={(e) => handleEditChange(key, e.target.value)}
                        className="font-semibold"
                      />
                    ) : (
                      <p className="text-xl font-bold">
                        {(item.annual_totals?.[key] || 0).toLocaleString()}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {/* Emission Records Detail */}
              {item.emission_records?.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm text-gray-500 mb-2">
                    {item.emission_records.length} individual records extracted
                  </p>
                  <div className="max-h-40 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="text-left p-2">Source</th>
                          <th className="text-left p-2">Scope</th>
                          <th className="text-right p-2">Quantity</th>
                          <th className="text-left p-2">Unit</th>
                          <th className="text-right p-2">tCO2e</th>
                        </tr>
                      </thead>
                      <tbody>
                        {item.emission_records.slice(0, 10).map((record, idx) => (
                          <tr key={idx} className="border-b">
                            <td className="p-2 capitalize">{record.source}</td>
                            <td className="p-2">{record.scope}</td>
                            <td className="p-2 text-right">{record.quantity?.toLocaleString()}</td>
                            <td className="p-2">{record.unit}</td>
                            <td className="p-2 text-right font-medium">{record.calculated_emissions_tco2e}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Year Selection */}
          <div className="flex items-center gap-4">
            <div>
              <label className="text-sm text-gray-500">Apply to Year</label>
              <Input
                type="number"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
                className="w-32"
                min={2020}
                max={2030}
              />
            </div>
            <div className="flex-1">
              <label className="text-sm text-gray-500">Notes (optional)</label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add review notes..."
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-4 border-t">
            <Button 
              variant="default" 
              onClick={() => handleAction('approve')}
              disabled={loading}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              <Check className="h-4 w-4 mr-2" />
              {loading ? 'Processing...' : editMode ? 'Apply with Corrections' : 'Approve & Apply'}
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleAction('reject')}
              disabled={loading}
              className="flex-1"
            >
              <X className="h-4 w-4 mr-2" />
              Reject
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Main Dashboard Component
const AIReviewDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');

  const fetchDashboard = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const data = await apiFetch(`/ai-review/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDashboard(data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load review dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleAction = async (module, recordId, action, options = {}) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/ai-review/item/${module}/${recordId}/action`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            action,
            year: options.year,
            notes: options.notes,
            corrections: options.corrections
          })
        }
      );
      
      const result = await response.json().catch(() => ({}));
      
      if (response.ok) {
        toast.success(result.message || `Item ${action}ed successfully`);
        fetchDashboard();
      } else {
        toast.error(result.detail || 'Action failed');
      }
    } catch (error) {
      toast.error('Failed to perform action');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const pendingItems = [
    ...(dashboard?.pending_items?.low_confidence || []),
    ...(dashboard?.pending_items?.medium_confidence || []),
    ...(dashboard?.pending_items?.high_confidence || [])
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="ai-review-dashboard">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Brain className="h-8 w-8 text-purple-600" />
          AI Data Review
        </h1>
        <p className="text-gray-500 mt-1">
          Review and approve AI-extracted data before it's applied to your ESG modules
        </p>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <StatCard
          title="Pending Review"
          value={dashboard?.dashboard?.total_pending_review || 0}
          icon={Clock}
          color="bg-yellow-100 text-yellow-600"
          subtext="Items awaiting your review"
        />
        <StatCard
          title="High Confidence"
          value={dashboard?.dashboard?.by_confidence?.high || 0}
          icon={CheckCircle2}
          color="bg-green-100 text-green-600"
          subtext="Ready for quick approval"
        />
        <StatCard
          title="Needs Attention"
          value={dashboard?.dashboard?.by_confidence?.low || 0}
          icon={AlertTriangle}
          color="bg-red-100 text-red-600"
          subtext="Low confidence - verify data"
        />
        <StatCard
          title="Recently Approved"
          value={dashboard?.recent_approved?.length || 0}
          icon={ThumbsUp}
          color="bg-blue-100 text-blue-600"
          subtext="In the last 7 days"
        />
      </div>

      {/* Module Breakdown */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Pending by Module</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            {Object.entries(dashboard?.dashboard?.by_module || {}).map(([module, count]) => {
              const config = MODULE_CONFIG[module];
              if (!config) return null;
              const Icon = config.icon;
              return (
                <div key={module} className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${config.bgColor}`}>
                    <Icon className={`h-5 w-5 ${config.color}`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{count}</p>
                    <p className="text-xs text-gray-500">{config.label}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="pending" className="gap-2">
            <Clock className="h-4 w-4" />
            Pending Review
            {pendingItems.length > 0 && (
              <Badge variant="secondary" className="ml-1">{pendingItems.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="approved" className="gap-2">
            <CheckCircle2 className="h-4 w-4" />
            Recently Approved
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          {pendingItems.length > 0 ? (
            <>
              {/* Low Confidence - Needs Attention */}
              {dashboard?.pending_items?.low_confidence?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium mb-3 flex items-center gap-2 text-red-600">
                    <AlertTriangle className="h-5 w-5" />
                    Needs Attention ({dashboard.pending_items.low_confidence.length})
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {dashboard.pending_items.low_confidence.map((item) => (
                      <ReviewItemCard
                        key={item.record_id}
                        item={item}
                        onAction={handleAction}
                        onViewDetail={setSelectedItem}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Medium Confidence */}
              {dashboard?.pending_items?.medium_confidence?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium mb-3 flex items-center gap-2 text-yellow-600">
                    <Eye className="h-5 w-5" />
                    Review Recommended ({dashboard.pending_items.medium_confidence.length})
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {dashboard.pending_items.medium_confidence.map((item) => (
                      <ReviewItemCard
                        key={item.record_id}
                        item={item}
                        onAction={handleAction}
                        onViewDetail={setSelectedItem}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* High Confidence */}
              {dashboard?.pending_items?.high_confidence?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium mb-3 flex items-center gap-2 text-green-600">
                    <CheckCircle2 className="h-5 w-5" />
                    High Confidence ({dashboard.pending_items.high_confidence.length})
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {dashboard.pending_items.high_confidence.map((item) => (
                      <ReviewItemCard
                        key={item.record_id}
                        item={item}
                        onAction={handleAction}
                        onViewDetail={setSelectedItem}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle2 className="h-12 w-12 mx-auto text-green-400 mb-4" />
                <p className="text-lg font-medium text-gray-700">All caught up!</p>
                <p className="text-gray-500">No pending items to review</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="approved">
          {dashboard?.recent_approved?.length > 0 ? (
            <div className="space-y-3">
              {dashboard.recent_approved.map((item, idx) => (
                <Card key={idx}>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium">{item.source_file}</p>
                          <p className="text-sm text-gray-500">
                            Applied to year {item.applied_year} • {new Date(item.applied_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline" className="bg-green-50">
                        {item.annual_totals?.total_emissions_tco2e?.toLocaleString()} tCO2e
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No recently approved items</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Detail Modal */}
      {selectedItem && (
        <ReviewDetailModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onAction={handleAction}
        />
      )}
    </div>
  );
};

export default AIReviewDashboard;
